<template>
  <div
    tabindex="0"
    class="tw-transition tw-border tw-border-gray dark:tw-border-dm-gray tw-rounded-2xl hover:tw-border-secondary dark:hover:tw-border-dm-secondary"
    :class="isEditing ? 'tw-border-secondary dark:tw-border-dm-secondary' : ''"
  >
    <div
      v-if="!isEditing"
      class="tw-cursor-pointer tw-p-4"
      @click="isEditing = true"
    >
      <div class="tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center tw-justify-between tw-gap-2 ">
        <div class="tw-flex tw-items-center">
          <span class="tw-font-medium tw-tracking-wider tw-leading-5">
            {{ permitDocument.document_type_name }}
          </span>
          <div class="tw-ml-2">
            <el-tooltip
              :content="permitDocument.document_type_description"
            >
              <div class="tw-ml-2">
                <icon-info class="tw-text-gray-dark dark:text-dm-gray-dark" />
              </div>
            </el-tooltip>
          </div>
        </div>
        <div class="tw-flex tw-items-center tw-gap-1">
          <el-tooltip
            :content="permitDocument.editable_by_foreigner ? $t('foreigners.permits.editable_foreigner') : $t('foreigners.permits.not_editable_foreigner')"
          >
            <div>
              <icon-f
                class="tw-w-4"
                :class="permitDocument.editable_by_foreigner ? 'tw-text-dark dark:tw-text-dm-dark' : 'tw-text-gray dark:tw-text-dm-gray'"
              />
            </div>
          </el-tooltip>
          <el-tooltip
            :content="permitDocument.editable_by_client ? $t('foreigners.permits.editable_client') : $t('foreigners.permits.not_editable_client')"
          >
            <div>
              <icon-c
                class="tw-w-4"
                :class="permitDocument.editable_by_client ? 'tw-text-dark dark:tw-text-dm-dark' : 'tw-text-gray dark:tw-text-dm-gray'"
              />
            </div>
          </el-tooltip>
          <i-badge
            :type="status.type"
            size="medium"
            class="tw-px-2 tw-ml-1"
          >
            {{ status.title }}
          </i-badge>
        </div>
      </div>
      <div
        v-if="permitDocument.document_name || permitDocument.note"
        class="tw-flex tw-items-center tw-flex-wrap tw-gap-x-8 tw-gap-y-1 tw-mt-0.5"
      >
        <a
          v-if="permitDocument.document_name"
          href="#"
          class="tw-flex tw-w-full tw-items-center tw-font-medium hover:tw-underline"
          @click.stop.prevent="downloadFileByUrlV2(`${baseURLv1}/foreigner/${$store.state.v2.admin.foreigners.foreigner.foreigner.id}/permit/${permit.id}/document/${permitDocument.id}/file/`, permitDocument.document_name, $axios)"
        >
          <icon-attachment class="tw-w-4 tw-mr-1.5" />
          <span class="tw-truncate tw-w-[calc(100%_-_1rem_-_6px)] tw-max-w-full">
            {{ permitDocument.document_name }}
          </span>
        </a>
        <div
          v-if="permitDocument.note"
          class="tw-text-gray-dark dark:tw-text-dm-gray-dark tw-flex tw-items-center tw-text-base"
        >
          <icon-screen-list class="tw-w-4 tw-mr-1.5" />
          {{ permitDocument.note }}
        </div>
      </div>
    </div>
    <div
      v-else
      class="tw-px-5 tw-py-5"
    >
      <el-form
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2"
        @submit.native.prevent="() => {}"
      >
        <el-form-item
          :label="$t('foreigners.permits.document_name')"
          prop="document_type_name"
        >
          <el-input
            v-model="form.document_type_name"
            :disabled="isObtained"
          />
        </el-form-item>

        <div class="tw-flex tw-gap-3">
          <i-upload
            :value="fileUploadInputValue"
            :disabled="isObtained"
            class="tw-w-1/2"
            multiple
            @changed="filesChanged"
          />

          <el-form-item
            :label="$t('common.filters.status')"
            class="tw-w-1/2"
          >
            <el-select
              v-model="form.status"
              :disabled="isObtained"
              filterable
              placeholder=""
            >
              <el-option
                v-for="item in foreignerPermitDocumentStatuses"
                :key="item.id"
                :label="$t(item.name)"
                :value="item.id"
              >
                <i-badge
                  :type="statusTypes[item.color]"
                  size="small"
                  is-circle
                  class="tw-px-2 tw-ml-1 tw-mr-3"
                />
                {{ $t(item.name) }}
              </el-option>
            </el-select>
          </el-form-item>
        </div>

        <el-form-item
          :label="$t('foreigners.permits.note')"
          prop="note"
        >
          <el-input
            v-model="form.note"
            :disabled="isObtained"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 5 }"
          />
        </el-form-item>

        <div class="tw-flex tw-gap-4 tw-justify-between tw-items-center tw-py-2">
          <div
            class="tw-flex tw-items-start md:tw-items-center tw-flex-col md:tw-flex-row-reverse tw-gap-4 tw-w-full tw-justify-between"
            :class="[
              isObtained ? 'tw-cursor-not-allowed' : '',
            ]"
          >
            <div class="tw-flex tw-gap-4">
              <el-switch
                v-model="form.editable_by_foreigner"
                :disabled="isObtained"
                :active-text="$t('foreigners.permits.editable_foreigner')"
              />
              <el-switch
                v-model="form.editable_by_client"
                :disabled="isObtained"
                :active-text="$t('foreigners.permits.editable_client')"
              />
            </div>
            <div
              :class="[
                isObtained ? 'tw-pointer-events-none' : '',
              ]"
            >
              <i-confirm-popover
                :cancel-text="$t('common.actions.cancel')"
                :submit-text="$t('common.actions.delete')"
                @submit="deleteDocument"
              >
                <template #reference>
                  <i-button
                    slot="reference"
                    :disabled="isObtained"
                  >
                    <icon-trash class="tw-w-4 tw-mr-1.5" />
                    {{ $t('foreigners.permits.delete_document') }}
                  </i-button>
                </template>
                <template #content>
                  <div>
                    {{ $t('foreigners.permits.document.delete_confirmation') }}
                  </div>
                </template>
              </i-confirm-popover>
            </div>
          </div>
          <div
            class="tw-flex tw-justify-between tw-items-center tw-gap-4"
          >
            <i-button
              size="medium"
              is-icon
              @click="isEditing = false"
            >
              <icon-angle-up />
            </i-button>
          </div>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import baseURLv1 from '@/services/APIConfig.js';
import { downloadFileByUrlV2 } from '@/helpers';
import { fileToBase64 } from '@/helpers';
import { mapState } from 'vuex';
import eventBus from '../../plugins/eventBus';

export default {
  props: {
    permit: {
      type: Object,
      required: true,
    },
    permitDocument: {
      type: Object,
      required: true,
    },
    isObtained: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEditing: false,
      isLoading: false,
      baseURLv1,
      form: {

      },
      formRules: {
        document_type_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
      },
      statusTypes: {
        yellow: 'warning',
        red: 'primary',
        green: 'success',
      },
    };
  },

  computed: {
    ...mapState({
      foreignerPermitDocumentStatuses: (state) => state.v2.shared.enums.enums.permit_document_statuses.map((status) => {
        let color = 'red';
        if (status.id === 20) {
          color = 'yellow';
        } else if (status.id === 30) {
          color = 'green';
        }
        return ({
          ...status,
          color,
        });
      }),
    }),
    status() {
      const status = this.$store.getters['v2/shared/enums/getForeignerPermitDocumentStatusById'](this.permitDocument.status);
      return {
        title: this.$t(status['key']),
        type: this.statusTypes[status.color],
      };
    },
    fileUploadInputValue() {
      if (this.form.delete_file) return undefined;
      if (this.form.document_name) return this.form.document_name;
      if (this.permitDocument.files?.length > 1) return `${this.$tc('common.field.files_uploaded_label', this.permitDocument.files.length )}`;
      return this.permitDocument.files?.[0]?.name;
    },
  },

  watch: {
    isEditing(isEditing) {
      if (isEditing) {
        this.form = {
          document_type_name: this.permitDocument.document_type_name,
          document_name: this.permitDocument.document_name,
          status: this.permitDocument.status,
          note: this.permitDocument.note,
          editable_by_foreigner: this.permitDocument.editable_by_foreigner,
          editable_by_client: this.permitDocument.editable_by_client,
          delete_file: this.permitDocument.delete_file,
        };
      }
    },

    form: {
      async handler(form, oldForm) {
        if (Object.keys(oldForm).length) {
          this.$store.commit('v2/admin/foreigners/foreigner/update', ['permitDocumentsUpdateDraft', {
            id: this.permitDocument.id,
            payload: {
              isUpdated: true,
              ...form,
            },
          }]);
        }
      },
      deep: true,
    },
  },

  mounted() {
    eventBus.on('collapse-document', this.handleSave);
  },
  beforeUnmount() {
    eventBus.off('collapse-document', this.handleSave);
  },

  methods: {
    downloadFileByUrlV2,
    handleSave(documentId) {
      if (documentId === this.permitDocument.id) {
        this.isEditing = false;
      }
    },
    async filesChanged(payload) {
      const files = await Promise.all(payload.map(async(source) => {
        const file = await fileToBase64(source).catch(() => null);
        return {
          file,
          name: source.name,
        };
      }));

      this.form = {
        ...this.form,
        delete_file: files.length ? undefined : true,
        files,
      };
    },

    deleteDocument() {
      this.form = {
        ...this.form,
        delete_document: true,
      };
      this.isEditing = false;
    },
  },
};
</script>

<style>

</style>
